import Modal from '../common/modal.tsx';
import { Directions } from './directions.tsx';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useMutation } from '@apollo/client';
import {
  TEST_ATTEMPT_QUESTION,
  UPDATE_TEST_ATTEMPT_ANSWER,
  UPDATE_TEST_ATTEMPT_MODULE,
} from '../../apollo/test-attempt.ts';
import QuestionsModal, { IQuestionData } from './questions-modal.tsx';
import QuestionContent from '../questions/question-content/question-content.tsx';
import {
  GetTestAttemptQuery,
  QuestionAnswerType,
  TestAttemptNextTrigger,
  TestAttemptStatus,
} from '../../apollo/__generated__/graphql.ts';
import CheckAnswers from './check-answers.tsx';
import TestAttemptFooter from './test-attempt-footer.tsx';
import TestAttemptHeader from './test-attempt-header.tsx';
import { capitalizeWords } from '../../utils/capitalize-words.ts';
import Loading from '../common/loading.tsx';
import { useFragment } from '../../apollo/__generated__';
import { useTraceSentryData } from '../common/use-trace-sentry-data.ts';
import { TestAttemptContext } from './test-attempt-provider/test-attempt-context.ts';
import { isEqual } from 'lodash';
import { useHighlight } from '../../contexts/highlight-provider/useHighlight.ts';
import { useQuestionHighlightVisibility } from '../../contexts/highlight-provider/use-question-highlight-visibility.ts';

type IProps = {
  attempt: GetTestAttemptQuery['testAttempt']['getAttempt'];
};

export const TestAttempt = ({ attempt }: IProps) => {
  const { highlightStickersToggle } = useHighlight();

  const { setAttemptId, attemptQuestions, setAttemptQuestions } =
    useContext(TestAttemptContext);
  const module = attempt.lastModule;
  const completedQuestions = useFragment(
    TEST_ATTEMPT_QUESTION,
    attempt.questions,
  );
  const getCompleted = useCallback(
    (questionId?: number) =>
      attemptQuestions.find((item) => item.questionId === questionId) ??
      completedQuestions.find((item) => item.questionId === questionId),
    [completedQuestions, attemptQuestions],
  );

  const getCurrentQuestion = useMemo(
    () =>
      module.questions.find(
        (question) => !getCompleted(question.question.id),
      ) || module.questions[0],
    [getCompleted, module.questions],
  );
  const { traceMutation } = useTraceSentryData();

  const [updateAnswer, { loading }] = useMutation(UPDATE_TEST_ATTEMPT_ANSWER, {
    // receive GET_TEST_ATTEMPT query after updating module only from cache to reduce network requests
    onQueryUpdated(query) {
      query.options.fetchPolicy = 'cache-only';
      return query;
    },
  });
  const [updateModule, { loading: moduleLoading }] = useMutation(
    UPDATE_TEST_ATTEMPT_MODULE,
    {
      // receive GET_TEST_ATTEMPT query after updating module only from cache to reduce network requests
      onQueryUpdated(query) {
        query.options.fetchPolicy = 'cache-only';
        return query;
      },
    },
  );
  const [currentModule, setCurrentModule] =
    useState<GetTestAttemptQuery['testAttempt']['getAttempt']['lastModule']>(
      module,
    );
  const [currentQuestion, setCurrentQuestion] = useState(getCurrentQuestion);
  const [marked, setMarked] = useState<boolean>();
  const [excludedChoices, setExcludedChoices] = useState<
    number[] | null | undefined
  >();
  const [isCheckAnswerScreen, setIsCheckAnswerScreen] = useState(false);
  const [isDirectionsModal, setIsDirectionsModal] = useState(false);
  const [seconds, setSeconds] = useState(attempt.remainingSeconds);
  const [isNewTimer, setIsNewTimer] = useState(false);
  const [stopTimer, setStopTimer] = useState(false);
  const [isQuestionsModal, setIsQuestionsModal] = useState(false);
  const [isCalc, setIsCalc] = useState(false);
  const questionsPanelRef = useRef<HTMLDivElement>(null);
  const moduleQuestions: IQuestionData[] = useMemo(() => {
    return (
      module.questions.map((q) => {
        const completed = getCompleted(q.question.id);
        return {
          id: q.id,
          isMarked: !!completed?.markedForReview,
          isCompleted: !!completed?.text || !!completed?.choice?.id,
          isCurrent: q.id === currentQuestion?.id,
        };
      }) || []
    );
  }, [currentQuestion?.id, getCompleted, module.questions]);
  const { hasQuestionStickers, toggleQuestionNotesVisible } = useQuestionHighlightVisibility(
    currentQuestion?.question?.id,
  );

  const getUserAnswer = (questionId?: number) => {
    const completedQuestion = getCompleted(questionId);
    if (!completedQuestion) return undefined;
    return completedQuestion?.text || completedQuestion?.choice?.id;
  };

  const [answer, setAnswer] = useState<number | string | undefined>(
    getUserAnswer(currentQuestion?.question.id),
  );
  const isAnswerExist =
    typeof answer !== 'undefined' && !!answer.toString().length;

  const closeModals = () => {
    setIsDirectionsModal(false);
    setIsQuestionsModal(false);
  };

  const updateAttempt = (sendAnswer?: boolean) => {
    const currentDate = new Date();
    const isChoiceType =
      currentQuestion?.question.answerType ===
      QuestionAnswerType.MultipleChoice;
    const text = !isChoiceType && sendAnswer ? String(answer) : null;

    const updatedQuestions = attemptQuestions.filter(
      (item) => item.questionId !== currentQuestion.question.id,
    );
    updatedQuestions.push({
      questionId: currentQuestion.question.id,
      markedForReview: marked,
      excludedChoices,
      text,
      choice: isChoiceType && sendAnswer ? { id: Number(answer) } : null,
    });
    setAttemptQuestions(updatedQuestions);

    traceMutation(updateAnswer, 'updateAnswer', {
      variables: {
        input: {
          id: attempt.id,
          remainingSeconds: seconds,
          question: {
            questionId: currentQuestion.question.id,
            ...(sendAnswer && {
              answeredAt: currentDate,
              timerAnsweredSeconds: seconds,
            }),
            choiceId: isChoiceType && sendAnswer ? Number(answer) : undefined,
            text,
            excludedChoices,
            markedForReview: marked,
            ...(marked && { markedForReviewAt: currentDate }),
          },
        },
      },
    });
  };

  const setNextModule = (nextModuleTrigger: TestAttemptNextTrigger) => {
    setStopTimer(true);
    traceMutation(updateModule, 'updateModule', {
      variables: {
        input: {
          id: attempt.id,
          remainingSeconds: seconds,
          nextModuleTrigger,
        },
      },
    });
  };

  const setNextQuestion = () => {
    const currentQuestionIndex = module.questions.findIndex(
      (question) => question.id === currentQuestion?.id,
    );

    if (currentQuestionIndex === -1) {
      console.error('Set next question error. Unexpected error');
      return;
    }

    // end of module
    if (module.questions.length === currentQuestionIndex + 1) {
      if (isCheckAnswerScreen) {
        if (loading) return;

        setNextModule(TestAttemptNextTrigger.Button);
      } else {
        updateAttempt(isAnswerExist);
        setIsCheckAnswerScreen(() => true);
      }

      return;
    }

    updateAttempt(isAnswerExist);
    const nextQuestion = module.questions[currentQuestionIndex + 1];
    setAnswer(getUserAnswer(nextQuestion?.question.id));
    setCurrentQuestion(nextQuestion);
  };

  const setPrevQuestion = () => {
    if (isCheckAnswerScreen) {
      const lastQuestion = module.questions[module.questions.length - 1];

      if (lastQuestion) {
        setCurrentQuestion(lastQuestion);
        setAnswer(getUserAnswer(lastQuestion.question.id));
      }

      setIsCheckAnswerScreen(() => false);
      return;
    }
    setCurrentQuestion((prevState) => {
      updateAttempt(isAnswerExist);
      const index = module.questions.findIndex(
        (question) => question.id === prevState?.id,
      );
      if (index === -1) {
        console.error('Set prev question error. Unexpected error');
        return prevState;
      }
      const prevQuestion = module.questions[index - 1];
      setAnswer(getUserAnswer(prevQuestion?.question.id));
      return prevQuestion;
    });
  };

  const jumpToQuestion = (id: number) => {
    setCurrentQuestion((prevState) => {
      const target = module?.questions.find((question) => question.id === id);
      if (!target) return prevState;
      setAnswer(getUserAnswer(target?.question.id));
      return target;
    });
    setIsCheckAnswerScreen(() => false);
    closeModals();
  };

  const onTimerOver = () => {
    closeModals();
    setNextModule(TestAttemptNextTrigger.Timer);
  };

  const openQuestionsModal = () => {
    if (isQuestionsModal) {
      setIsQuestionsModal(false);
    } else {
      updateAttempt(isAnswerExist);
      setIsQuestionsModal(true);
    }
  };

  useEffect(() => {
    if (!isQuestionsModal) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (questionsPanelRef.current?.contains(e.target as Node)) return;
      if ((e.target as HTMLElement).closest?.('[data-questions-toggle]')) return;
      setIsQuestionsModal(false);
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsQuestionsModal(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isQuestionsModal]);

  useEffect(() => {
    setAttemptId(attempt.id);
  }, [attempt.id, setAttemptId]);

  useEffect(() => {
    const completed = getCompleted(currentQuestion?.question.id);
    setExcludedChoices(completed?.excludedChoices);
    setMarked(completed?.markedForReview);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion]);

  useEffect(() => {
    closeModals();
    const seconds = attempt.remainingSeconds;
    if (seconds && seconds > 0) setSeconds(seconds);
    if (currentModule && currentModule.id === module.id) return;
    if (module.questions[0]) {
      setIsCheckAnswerScreen(() => false);
      setIsNewTimer(true);
      const nextQuestion = module.questions[0];
      setCurrentQuestion(nextQuestion);
      setCurrentModule(module);
      setAnswer(getUserAnswer(nextQuestion.question.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module.id, currentModule.id]);

  useEffect(() => {
    const updatedQuestions = attemptQuestions.filter((item) => {
      const completed = completedQuestions.find(
        (q) => q.questionId === item.questionId,
      );
      if (completed) {
        return !(
          completed.markedForReview === item.markedForReview ||
          isEqual(completed.excludedChoices, item.excludedChoices) ||
          completed.text === item.text ||
          completed.choice?.id === item.choice?.id
        );
      }
      return !completed;
    });
    setAttemptQuestions(updatedQuestions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedQuestions, setAttemptQuestions]);

  return (
    <>
      {moduleLoading && (
        <div
          className={
            'absolute left-0 top-0 z-10 h-full w-full bg-black/40 backdrop-blur-sm'
          }
        >
          <Loading className={'mx-0 mt-0 h-full w-full'} />
        </div>
      )}
      <div>
        <div
          className={
            'attempt-viewport-height fixed inset-0 grid min-h-0 w-full grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden bg-white'
          }
        >
          <TestAttemptHeader
            openQuestionsModal={openQuestionsModal}
            questions={module.questions}
            currentQuestionId={currentQuestion?.id}
            attempt={attempt}
            isLoading={loading}
            setSeconds={setSeconds}
            seconds={seconds}
            onTimerOver={onTimerOver}
            openDirections={() => setIsDirectionsModal(true)}
            newTimer={isNewTimer}
            setNewTimer={setIsNewTimer}
            stopTimer={stopTimer}
            setStopTimer={setStopTimer}
            questionId={currentQuestion?.question?.id}
            isMath={currentQuestion?.question.section?.name === 'math'}
            isCalc={isCalc}
            onToggleCalculator={() => setIsCalc((prevState) => !prevState)}
          />

          {isCheckAnswerScreen ? (
            <CheckAnswers
              jumpToQuestion={jumpToQuestion}
              questions={moduleQuestions}
            />
          ) : (
            <QuestionContent
              highlightStickersToggle={highlightStickersToggle}
              hasQuestionStickers={hasQuestionStickers}
              onToggleNoteVisibility={toggleQuestionNotesVisible}
              isShowHighLights={attempt.status !== TestAttemptStatus.Completed}
              questionId={currentQuestion?.question?.id}
              prompt={currentQuestion?.question.prompt}
              content={currentQuestion?.question.content}
              setMark={setMarked}
              isMark={marked}
              setExcludedChoices={setExcludedChoices}
              choicesData={
                currentQuestion?.question.answerType ===
                QuestionAnswerType.MultipleChoice
                  ? currentQuestion.question.choices
                  : undefined
              }
              excludedChoices={excludedChoices}
              setAnswer={setAnswer}
              answer={answer}
              isMath={currentQuestion?.question.section?.name === 'math'}
              questionNumber={currentQuestion.order + 1}
              isModalMode={false}
              stickyBottomActions
              isCalc={isCalc}
              setIsCalc={setIsCalc}
            />
          )}

          <TestAttemptFooter
            isCheckAnswerScreen={isCheckAnswerScreen}
            isQuestionsModalOpen={isQuestionsModal}
            openQuestionsModal={openQuestionsModal}
            questions={module.questions}
            setNextQuestion={setNextQuestion}
            setPrevQuestion={setPrevQuestion}
            currentQuestionId={currentQuestion?.id}
          />
        </div>

        <Modal
          onClose={closeModals}
          isOpen={isDirectionsModal}
          title={'Directions'}
        >
          <Directions isMath={true} />
        </Modal>

        {isQuestionsModal && (
          <div
            ref={questionsPanelRef}
            className="fixed bottom-12 left-1/2 z-50 w-fit -translate-x-1/2 overflow-visible rounded-[28px] bg-white px-8 pb-6 pt-6 shadow-[0_10px_30px_rgba(15,23,42,0.18)]"
          >
            <div className="my-1 flex items-center justify-between px-1 font-semibold">
              <span>{`Section ${module.testSection.order + 1}, Module ${module.order + 1}: ${capitalizeWords(module?.testSection.section.name)}`}</span>
              <button onClick={() => setIsQuestionsModal(false)} className="ml-4">
                <XMarkIcon className="h-8 w-8 rounded p-1 text-black hover:bg-light-blue" />
              </button>
            </div>
            <hr className="opacity-10" />
            <div className="pt-1">
              <QuestionsModal
                questions={moduleQuestions}
                jumpToQuestion={jumpToQuestion}
                onGoToReviewPage={() => {
                  setIsCheckAnswerScreen(true);
                  closeModals();
                }}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};
